class TimecodeInput extends HTMLElement {
    constructor() {
        super();
    }

    static get boundAttributes() {
        return ['value', "timebase", "form-name", "size", "drop-frame"];
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this[attrName] = newValue;

        if (attrName === 'value') {
            this.syncTimecodeFields();
        }
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open'});

            // Append site stylesheet
            const styleSheet = document.createElement('link');
            styleSheet.setAttribute('rel', 'stylesheet');
            styleSheet.setAttribute('href', '/dist/style.css');
            this.shadowRoot.appendChild(styleSheet);

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'timecode-input-wrapper';
            this._wrapper.style.display = 'inline-block';

            for (let i = 0; i < 4; i++) {
                const element = document.createElement('input');
                element.className = 'timecode_txt';
                element.setAttribute('type', 'text');
                element.setAttribute('autocomplete', 'off');
                this._wrapper.appendChild(element);

                element.addEventListener("keydown", (e) => this.textKeyDown(e));
                element.addEventListener("keyup", (e) => this.textKeyUp(e));
                element.addEventListener("focus", (e) => this.textboxFocus(e));
                element.addEventListener("blur", (e) => this.textboxBlur(e));
                element.addEventListener("paste", (e) => this.textboxPaste(e));

                if (this.size === 'small' || this.size === 'mini') {
                    element.classList.add(this.size);
                }

                const colon = document.createElement('span');
                colon.textContent = ':';

                if (i < 3) {
                    this._wrapper.appendChild(colon);
                }

                switch (i) {
                    case 0:
                        element.id = 'hours';
                        this._hours = element;
                        break;

                    case 1:
                        element.id = 'mins';
                        this._mins = element;
                        break;

                    case 2:
                        element.id = 'secs';
                        this._secs = element;
                        this._frameColon = colon;
                        break;

                    case 3:
                        element.id = 'frames';
                        this._frames = element;
                        break;
                }
            }

            this.shadowRoot.appendChild(this._wrapper);

            this._slot = document.createElement('slot');
            this.shadowRoot.appendChild(this._slot);

            this.render();

            this._hidden_txt = document.createElement('input');
            this._hidden_txt.setAttribute("type", "hidden");
            this._hidden_txt.setAttribute("value", this.value);

            if (this.formname) {
                this._hidden_txt.setAttribute("name", this.formname);
            }

            this.appendChild(this._hidden_txt);

            this.syncTimecodeFields();
        }
    }

    disconnectedCallback() {
//        this.parseTextInput();
    }

    get value() {
        return this.getAttribute('value');
    }

    set value(_value) {
        this.setAttribute("value", _value);
    }

    get timebase() {
        return this.getAttribute("timebase");
    }

    set timebase(_timebase) {
        this.setAttribute("timebase", _timebase);
    }

    get formname() {
        return this.getAttribute("form-name");
    }

    set formname(_formname) {
        this.setAttribute("form-name", _formname);

        this._hidden_txt.setAttribute("name", _formname);
    }

    get size() {
        return this.getAttribute("size");
    }

    set size(_size) {
        this.setAttribute("size", _size);
    }

    get dropframe() {
        return this.getAttribute("drop-frame");
    }

    set dropframe(_dropframe) {
        this.setAttribute("drop-frame", _dropframe);
    }

    textboxBlur(e) {
        this.validateTextboxValue(e.target);
    }

    textboxFocus(e) {
        e.target.select();
    }

    validateTextboxValue(textbox) {
        let timebase = parseInt(this.timebase);
        if (isNaN(timebase) || timebase < 1) timebase = 24;

        let intVal = parseInt(textbox.value);
        if (isNaN(intVal)) intVal = 0;

        // Determine correct range
        if (intVal < 0) intVal = 0;
        let maxVal = 59;

        if (textbox.id === "frames") {
            maxVal = timebase - 1;
        } else if (textbox.id === "hours") {
            maxVal = 23;
        }

        if (intVal > maxVal) intVal = maxVal;

        textbox.value = this.padNumber(intVal);
        this.updateTimecode();
    }

    textKeyDown(e) {
        const textbox = e.target;
        const idSequence = ["hours", "mins", "secs", "frames"];

        const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End', 'Tab', 'Backspace', 'Delete', 'Alt', 'Meta', 'Control', 'Shift'];

        const isNumericKey = (RegExp('[0-9]').test(e.key));

        // Don't handle if sequence contains ctrl or cmd
        if (e.ctrlKey || e.metaKey) return;

        if (allowedKeys.indexOf(e.key) < 0 && !isNumericKey) {
            console.log("Ignoring key " + e.key);
            e.preventDefault();
            return;
        }

        switch (e.key) {
            case 'ArrowLeft':
                if (textbox.selectionStart === 0) { // Left-most cursor position
                    const seqPos = idSequence.indexOf(e.target.id);

                    if (seqPos > 0) {
                        const newTargetId = idSequence[seqPos - 1];
                        const newTarget = this.shadowRoot.getElementById(newTargetId);
                        newTarget.focus();
                        setTimeout(() => {
                            newTarget.select();
//                            newTarget.setSelectionRange(0, newTarget.value.length);
                        }, 1);
                    }
                }
                break;

            case 'ArrowRight':
            case ':':
                if (textbox.selectionStart >= 2) { // Right-most cursor position
                    const seqPos = idSequence.indexOf(e.target.id);

                    if (seqPos >= 0 && seqPos < idSequence.length - 1) {
                        const newTargetId = idSequence[seqPos + 1];
                        const newTarget = this.shadowRoot.getElementById(newTargetId);
                        newTarget.focus();
                        setTimeout(() => {
                            newTarget.setSelectionRange(0, newTarget.value.length, "backward");
                        }, 1);
                    }
                }
                break;

            case 'ArrowUp': {
                // Increment field value
                let val = parseInt(e.target.value);
                if (isNaN(val)) val = 0;

                let diff = 1;
                if (e.shiftKey) {
                    diff = 10;
                }

                val += diff;
                if (val > 59) val = 59;

                e.target.value = val;
                this.validateTextboxValue(e.target);
                break;
            }

            case 'ArrowDown': {
                // Decrement field value
                let val = parseInt(e.target.value);
                if (isNaN(val)) val = 0;

                let diff = 1;
                if (e.shiftKey) {
                    diff = 10;
                }

                val -= diff;
                if (val < 0) val = 0;

                e.target.value = val;
                this.validateTextboxValue(e.target);
                break;
            }
        }
    }

    textKeyUp(e) {
        const textbox = e.target;
        const idSequence = ["hours", "mins", "secs", "frames"];

        const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End', 'Tab', 'Backspace', 'Delete', 'Alt', 'Meta'];

        const isNumericKey = (RegExp('[0-9]').test(e.key));

        if (!isNumericKey) {
            return;
        }

        switch (e.key) {
            default:
                const textLength = textbox.value.length;
                const selectionStart = textbox.selectionStart;
                const seqPos = idSequence.indexOf(e.target.id);

                if (textLength >= 2) {
                    // Go to next text box as you type (only if cursor is at the end)
                    if (selectionStart >= textLength - 1) {
                        // Determine next text box
                        if (seqPos >= 0 && seqPos < idSequence.length - 1) {
                            const newTargetId = idSequence[seqPos + 1];
                            const newTarget = this.shadowRoot.getElementById(newTargetId);
                            textbox.value = textbox.value.substr(0, 2);

                            setTimeout(() => {
                                newTarget.focus();
                            }, 1);

                        } else if (seqPos === idSequence.length - 1) {
                            const newTargetId = idSequence[idSequence.length - 1];
                            const newTarget = this.shadowRoot.getElementById(newTargetId);
                            setTimeout(() => {
                                newTarget.focus();
                            newTarget.setSelectionRange(0, 2);
                            }, 1);
                        }

                    } else { // Truncate if cursor is at start
                        if (isNumericKey) {
                            e.preventDefault();
                            textbox.value = textbox.value.substr(0, 2);
                            textbox.setSelectionRange(selectionStart, selectionStart);
                        }
                    }
                }
                break;
        }
    }

    textboxPaste(e) {
        // Check if user pasted a full timecode string
        const str = e.clipboardData.getData('Text');
        if (str !== null) {
            e.preventDefault();

            const components = str.split(":");

            if (components.length === 4) {
                this.value = str;
                this.syncTimecodeFields();
            } else {
                let val = str;
                if (val.length > 2) val = val.substr(0, 2);

                e.target.setAttribute('value', parseInt(val));
                this.validateTextboxValue(e.target);
            }
        }
    }

    syncTimecodeFields() {
        const val = this.value.replace(';', ':');
        const components = val.split(":");

        if (components.length === 4) {
            this._hours.value = components[0];
            this.validateTextboxValue(this._hours);

            this._mins.value = components[1];
            this.validateTextboxValue(this._mins);

            this._secs.value = components[2];
            this.validateTextboxValue(this._secs);

            this._frames.value = components[3];
            this.validateTextboxValue(this._frames);
        }
    }

    updateTimecode() {
        const hours = this._hours.value;
        const mins = this._mins.value;
        const secs = this._secs.value;
        const frames = this._frames.value;

        this.value = hours + ":" + mins + ":" + secs + ":" + frames;
        this.querySelector(`input[type="hidden"]`).value = this.value;
    }

    padNumber(num) {
        return (num < 10 ? "0":"") + num;
    }

    render() {
        if (!this.value) this.value = "00:00:00:00";
        let components = this.value.split(":");

        if (components.length > 4) {
            components = components.slice(0, 4);
        } else if (components.length < 4) {
            components = ["00", "00", "00", "00"];
        }

        this._hours.setAttribute('value', components[0]);
        this._mins.setAttribute('value', components[1]);
        this._secs.setAttribute('value', components[2]);
        this._frames.setAttribute('value', components[3]);

        this._frameColon.textContent = !!this.dropframe ? ';' : ':';
    }
}

customElements.define('timecode-input', TimecodeInput);